import React, { useEffect, useState } from 'react';
import styles from './Accordion.module.css';
import InvisibleButton from '../../Design/Button/Invisible/Invisible';
import CreateForm from '../Forms/CreateForm';

const Accordion = ({ items, keepOthersOpen, formData, setFormData, handleSubmit }) => {
  const [accordionItems, setAccordionItems] = useState([]);

  useEffect(() => {
    if (items) {
      // Initialize with nested subItems also having toggled property
      const initializeWithSubItems = (itemsList) => {
        return itemsList.map(item => ({
          ...item,
          toggled: false,
          subItems: item.subItems ? initializeWithSubItems(item.subItems) : undefined
        }));
      };
      
      setAccordionItems(initializeWithSubItems(items));
    }
  }, [items]);

  // Toggle accordion item logic with support for nested items
  function handleAccordionToggle(clickedItem, path = []) {
    setAccordionItems(prevItems => {
      return updateItemsAtPath(prevItems, path, clickedItem.id);
    });
  }

  // Helper function to update nested items at specified path
  const updateItemsAtPath = (items, path, targetId) => {
    return items.map(item => {
      if (item.id === targetId) {
        // Toggle this item
        return { ...item, toggled: !item.toggled };
      } else if (path.length > 0 && item.id === path[0] && item.subItems) {
        // Continue down the path
        return {
          ...item,
          subItems: updateItemsAtPath(item.subItems, path.slice(1), targetId)
        };
      } else if (!keepOthersOpen && path.length === 0) {
        // Close other top-level items if not keeping others open
        return { ...item, toggled: false };
      }
      return item;
    });
  };

  // Function to render content based on its type
  const renderContent = (item) => {
    switch(item.type) {
      case 'form':
        return (
          <CreateForm 
            formData={formData} 
            setFormData={setFormData} 
            onSubmit={handleSubmit}
            text={item.buttonText || "Submit"}
            fields={item.formFields || []}
          />
        );
      case 'text':
      default:
        return <div>{item.content}</div>;
    }
  };

  // Function to render accordion items (can be recursive)
  const renderAccordionItems = (items, path = []) => {
    return items.map(item => (
      <div
        className={`${styles.accordion} ${item.toggled ? styles.toggled : ''}`}
        key={item.id}
      >
        <InvisibleButton 
          type="button"
          onClick={() => handleAccordionToggle(item, path)}
          className={styles.toggle}
        >
          <p className={styles.buttonLabel}>{item.label}</p>
          <div className={styles.directionIndicator}>
            {item.toggled ? '-' : '+'}
          </div>
        </InvisibleButton>

        {item.toggled && (
          <div className={styles.contentParent}>
            <div className={styles.content}>
              {/* Render content based on type */}
              {renderContent(item)}
              
              {/* Render nested accordion if there are subItems */}
              {item.subItems && item.subItems.length > 0 && (
                <div className={styles.nestedAccordion}>
                  {renderAccordionItems(item.subItems, [...path, item.id])}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className={styles.accordionParent}>
      {renderAccordionItems(accordionItems)}
    </div>
  );
};

export default Accordion;